import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { storage } from "@/lib/storage";
import toast from "react-hot-toast";
import {
  LogOut, ChevronDown, MoreHorizontal, UtensilsCrossed, ShieldCheck,
  CalendarDays, Users, Phone, Navigation, Menu, X,
  UserPlus,
  BadgePercent,
  Cake,
  Smartphone,
  CreditCard,
  Repeat,
  History,
  Megaphone,
} from "lucide-react";
import DeliveryLiveTracker from "@/components/delivery/DeliveryLiveTracker";
import { useDispatch, useSelector } from 'react-redux';
import { updateUserData } from "@/lib/store/slices/UserSlice";
import { RootState } from "@/lib/store";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { setRefreshTokenCookie } from "@/lib/clientCookie";
import { logoutService } from '@/services/auth';
import { clearPermissions } from '@/lib/store/slices/PermissionsSlice';

function getDashboardPath(role?: string): string {
  const r = role?.toLowerCase().replace(/[\s_]+/g, '');
  if (r === 'parent') return '/dashboard';
  if (r === 'superuser') return '/superuser-dashboard';
  if (r === 'zonemanager') return '/zone-manager-dashboard';
  if (r === 'branchincharge') return '/branch-incharge-dashboard';
  return '/admin-dashboard';
}

const NAV_ITEMS: { label: string; path: string; screens: string[]; roles?: string[] }[] = [
  { label: "Dashboard", path: "__dashboard__", screens: [] },
  { label: "Users", path: "/user-management", screens: ["employees", "resgistered profile", "pending payments"] },
  { label: "Track Delivery", path: "/track-delivery", screens: ["location tracking"] },
  { label: "Masters", path: "/masters", screens: ["states", "cities", "schools", "zones", "branches", "roles", "ranges"] },
  { label: "Verifications", path: "/verification", screens: ["verified child"] },
  { label: "Leads", path: "/leads", screens: ["leads"] },
  { label: "Pickups", path: "/pickups", screens: ["pickups"], roles: ["branchincharge", "zonemanager"] },
  { label: "Subscription Data", path: "/subscription-data", screens: ["subscription data"] },
];

const MORE_ITEMS = [
  { key: "canSeeLunchUpdates", screen: "lunch update", path: "/lunch-updates", label: "Lunch Updates", Icon: UtensilsCrossed, color: "text-mlb-gold" },
  { key: "canSeeHolidays", screen: "holidays", path: "/holidays", label: "Holidays", Icon: CalendarDays, color: "text-mlb-gold" },
{ key: "canSeeNotices", screen: "notices", path: "/notices", label: "Notices", Icon: Megaphone, color: "text-mlb-gold" },
  { key: "canSeeSubscriptions", label: "Subscriptions", path: "/pricing-management", screen: "plans", Icon: Repeat, color: "text-mlb-green" },
  { key: "canSeeHistory", label: "History", path: "/history", screen: "subscriptionplanshistory", Icon: History, color: "text-mlb-green" },
  { key: "canSeeTeleRecord", screen: "telerecords", path: "/tele-record", label: "Tele Record", Icon: Phone, color: "text-mlb-gold" },
  { key: "canSeeAssignDelivery", screen: "delivery assign", path: "/assignment", label: "Assign Delivery", Icon: UserPlus, color: "text-mlb-green" },
  { key: "canSeeReferral", screen: "referral", path: "/referral", label: "Referrals", Icon: UserPlus, color: "text-mlb-green" },
  { key: "canSeeOffers", screen: "offers", path: "/offers", label: "Offers", Icon: BadgePercent, color: "text-mlb-green" },
  { key: "canSeeSpecialOffers", screen: "special offer", path: "/special-offers", label: "Special Offers", Icon: BadgePercent, color: "text-mlb-green" },
  { key: "canSeeTrailOffers", screen: "trails offer", path: "/trail-offer", label: "Trail Offers", Icon: BadgePercent, color: "text-mlb-green" },
  { key: "canSeeUpcomingBirthdays", screen: "upcoming birthdays", path: "/upcoming-birthdays", label: "Birthdays", Icon: Cake, color: "text-mlb-green" },
  { key: "canSeePaymentSummary", screen: "payment summary", path: "/payment-summary", label: "Payments Data", Icon: CreditCard, color: "text-mlb-green" },
  { key: "canSeePaymentList", screen: "payment list", path: "/payment-list", label: "Payments", Icon: CreditCard, color: "text-mlb-green" },
] as const;

export default function AdminHeader() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const userDetails = useCurrentUser();
  const dispatch = useDispatch();
  const { loaded: permissionsLoaded, data: permissions } = useSelector((state: RootState) => state.permissions);
  const noPermissions = !permissionsLoaded;

  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  const role = (userDetails?.role as string | undefined) ?? '';
  const dashboardPath = getDashboardPath(role);
  const normalizedRole = role.toLowerCase().replace(/[\s_]+/g, '');
  let isLoggingOut = false;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setShowMobileMenu(false); }, [pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = showMobileMenu ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showMobileMenu]);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    isLoggingOut = true;

    try {
      const refreshToken = storage.getRefreshToken();

      if (refreshToken) {
        await logoutService(refreshToken);
      }
    } catch (err) {
      console.error("Logout API failed:", err);
    } finally {
      storage.setRefreshToken('');
      setRefreshTokenCookie('');
      storage.logout();
      dispatch(updateUserData(null));
      dispatch(clearPermissions());

      toast.success("Logged out successfully");
      navigate("/login", { replace: true });

      isLoggingOut = false;
    }
  };

  const canSeeNav = (screens: string[]) =>
    noPermissions || screens.some(k => k in permissions && permissions[k]?.Can_View !== false);

  const visibleNavItems = NAV_ITEMS
    .map(item => ({ ...item, path: item.path === '__dashboard__' ? dashboardPath : item.path }))
    .filter(item => !item.roles || item.roles.includes(normalizedRole))
    .filter(item => item.screens.length === 0 || canSeeNav(item.screens));

  const isSuperuser = !!(userDetails as any)?.isSuperuser;

  const visibleMoreItems = MORE_ITEMS.filter(item =>
    noPermissions || (item.screen in permissions && permissions[item.screen]?.Can_View !== false)
  );

  const showMoreDropdown = visibleMoreItems.length > 0 || isSuperuser;

  return (
    <>
      <header className="sticky top-0 z-40" style={{ background: 'linear-gradient(135deg, #1e2530 0%, #2d3748 50%, #1e2530 100%)', boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.05)' }}>

        {/* Top accent line */}
        <div className="h-0.5 w-full" style={{ background: 'linear-gradient(90deg, transparent, #d4a017, #f0c040, #d4a017, transparent)' }} />

        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">

          {/* Logo & Brand */}
          <Link to={dashboardPath} className="flex items-center gap-2.5 flex-shrink-0 group">
            <div className="w-10 h-10 sm:w-11 sm:h-11 relative rounded-xl overflow-hidden border border-mlb-gold/30 group-hover:border-mlb-gold/60 transition-colors" style={{ boxShadow: '0 0 12px rgba(212,160,23,0.15)' }}>
              <img src="/mlblogo.jpeg" alt="MLB" className="w-full h-full object-cover" />
            </div>
            <div className="xs:block lg:hidden xl:block">
              <h1 className="text-base sm:text-lg font-extrabold text-white tracking-tight leading-tight">Mera Lunch Box</h1>
              <p className="text-mlb-gold text-[9px] font-semibold tracking-widest uppercase leading-none">Taaza Khana, Ghar Ka Khana</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {visibleNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-semibold transition-all px-3 py-2 rounded-lg whitespace-nowrap ${pathname === item.path
                    ? 'text-[#1e2530] bg-mlb-gold shadow-md'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
              >
                {item.label}
              </Link>
            ))}

            {/* More dropdown */}
            {showMoreDropdown && (
              <div className="relative" ref={moreMenuRef}>
                <button
                  onClick={() => setShowMoreMenu(prev => !prev)}
                  className={`flex items-center gap-1 text-sm font-semibold transition-all px-3 py-2 rounded-lg ${showMoreMenu ? 'text-[#1e2530] bg-mlb-gold' : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                >
                  More <MoreHorizontal className="w-3.5 h-3.5" />
                </button>
                {showMoreMenu && (
                  <div
                    className="absolute left-0 mt-2 w-52 rounded-xl z-50"
                    style={{ background: '#1e2530', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
                  >
                    <div className="p-1.5 space-y-0.5 max-h-[calc(100vh-88px)] overflow-y-auto overscroll-contain rounded-xl">
                      {visibleMoreItems.map(({ path, label, Icon, color }) => (
                        <button
                          key={path}
                          onClick={() => { navigate(path); setShowMoreMenu(false); }}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors hover:bg-white/8 ${pathname === path ? 'bg-white/10 text-white' : 'text-white/75 hover:text-white'}`}
                        >
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.07)' }}>
                            <Icon className={`w-3.5 h-3.5 ${color}`} />
                          </div>
                          <span className="font-medium">{label}</span>
                        </button>
                      ))}
                      {isSuperuser && (
                        <>
                          <button
                            onClick={() => { navigate('/mobile-updates'); setShowMoreMenu(false); }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors hover:bg-white/8 ${pathname === '/mobile-updates' ? 'bg-white/10 text-white' : 'text-white/75 hover:text-white'}`}
                          >
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.07)' }}>
                              <Smartphone className="w-3.5 h-3.5 text-mlb-green" />
                            </div>
                            <span className="font-medium">Mobile Updates</span>
                          </button>
                          <button
                            onClick={() => { navigate('/permissions'); setShowMoreMenu(false); }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-white/75 hover:text-white rounded-lg transition-colors hover:bg-white/8"
                          >
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.07)' }}>
                              <ShieldCheck className="w-3.5 h-3.5 text-mlb-green" />
                            </div>
                            <span className="font-medium">Permissions</span>
                          </button>

                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Right — user + hamburger */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* User menu — desktop */}
            <div className="relative group hidden sm:block">
              <button className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-white/10 transition-colors">
                <div className="w-8 h-8 bg-mlb-gold rounded-full flex items-center justify-center text-sm font-bold text-[#1e2530] flex-shrink-0">
                  {(userDetails?.username as string | undefined)?.[0]?.toUpperCase()}
                </div>
                <div className="text-left hidden md:block">
                  <p className="text-sm text-white font-semibold leading-tight">{userDetails?.username as string}</p>
                  {role && <p className="text-[10px] text-mlb-gold leading-tight capitalize">{role}</p>}
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-white/50" />
              </button>

              <div className="absolute right-0 mt-2 w-52 rounded-xl overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50" style={{ background: '#1e2530', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
                <div className="p-4 border-b border-white/8">
                  <p className="text-sm font-bold text-white">{userDetails?.username as string}</p>
                  <p className="text-xs text-white/50 truncate mt-0.5">{(userDetails as any)?.email}</p>
                  {role && <span className="mt-2 inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-mlb-gold text-[#1e2530] capitalize">{role}</span>}
                </div>
                <div className="p-2">
                  <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-white/8 hover:text-red-300 rounded-lg transition-colors">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              </div>
            </div>

            {/* Hamburger — mobile/tablet */}
            <button
              onClick={() => setShowMobileMenu(prev => !prev)}
              className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg hover:bg-white/10 transition-colors text-white/80 hover:text-white"
            >
              {showMobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Drawer — absolute so it overlays content without pushing it down */}
        {showMobileMenu && (
          <div className="lg:hidden absolute top-full left-0 right-0 z-50 border-t border-white/8 shadow-2xl max-h-[calc(100vh-60px)] overflow-y-auto" style={{ background: '#1a2030' }}>
            <div className="max-w-screen-2xl mx-auto px-4 py-3 space-y-1">

              {/* User info (mobile) */}
              <div className="flex items-center gap-3 p-3 rounded-xl mb-3" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <div className="w-10 h-10 bg-mlb-gold rounded-full flex items-center justify-center text-base font-bold text-[#1e2530] flex-shrink-0">
                  {(userDetails?.username as string | undefined)?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{userDetails?.username as string}</p>
                  {role && <p className="text-xs text-mlb-gold capitalize">{role}</p>}
                </div>
                <button onClick={handleLogout} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-400 hover:bg-white/10 transition-colors">
                  <LogOut className="w-3.5 h-3.5" />
                  Logout
                </button>
              </div>

              {/* Nav items */}
              {visibleNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${pathname === item.path
                      ? 'bg-mlb-gold text-[#1e2530]'
                      : 'text-white/75 hover:text-white hover:bg-white/8'
                    }`}
                >
                  {item.label}
                </Link>
              ))}

              {/* More items */}
              {(visibleMoreItems.length > 0 || isSuperuser) && (
                <div className="pt-2 border-t border-white/8 mt-2 space-y-1">
                  <p className="px-4 text-[10px] font-bold text-white/30 uppercase tracking-widest py-1">More</p>
                  {visibleMoreItems.map(({ path, label, Icon, color }) => (
                    <Link
                      key={path}
                      to={path}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${pathname === path ? 'bg-mlb-gold text-[#1e2530]' : 'text-white/75 hover:text-white hover:bg-white/8'
                        }`}
                    >
                      <Icon className={`w-4 h-4 ${pathname === path ? 'text-[#1e2530]' : color}`} />
                      {label}
                    </Link>
                  ))}
                  {isSuperuser && (
                    <>
                      <Link
                        to="/mobile-updates"
                        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${pathname === '/mobile-updates' ? 'bg-mlb-gold text-[#1e2530]' : 'text-white/75 hover:text-white hover:bg-white/8'
                          }`}
                      >
                        <Smartphone className={`w-4 h-4 ${pathname === '/mobile-updates' ? 'text-[#1e2530]' : 'text-mlb-green'}`} />
                        Mobile Updates
                      </Link>
                      <Link
                        to="/permissions"
                        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${pathname === '/permissions' ? 'bg-mlb-gold text-[#1e2530]' : 'text-white/75 hover:text-white hover:bg-white/8'
                          }`}
                      >
                        <ShieldCheck className={`w-4 h-4 ${pathname === '/permissions' ? 'text-[#1e2530]' : 'text-mlb-green'}`} />
                        Permissions
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </header>
      <DeliveryLiveTracker />
    </>
  );
}